import { CRM_API_BASE_URL, CRM_AUTH_TOKEN, buildCrmApiUrl } from './apiConfig';

// Every request to the sales CRM carries two things:
//   Authorization  the static app token (proves the call comes from the sales app)
//   X-User-Token   the obalgo login JWT (proves WHICH number is logged in)
// The CRM resolves the caller's role (admin / leader / executive) from the JWT; the
// ?number= some older calls still send is ignored for identity.
//
// The current "view" is appended too, so one switch in the header changes every screen:
//   view=me       a leader working their personal leads
//   member=<name> a leader/admin narrowed to one person
//   team=<leader> an admin narrowed to one team

const view = { me: false, member: '', team: '' };
const listeners = new Set();

export const getView = () => ({ ...view });

export const setView = (next) => {
  view.me = Boolean(next.me);
  view.member = next.member || '';
  view.team = next.team || '';
  listeners.forEach((fn) => fn(getView()));
};

export const onViewChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const withView = (url) => {
  const params = [];
  if (view.me) params.push('view=me');
  if (view.member) params.push(`member=${encodeURIComponent(view.member)}`);
  if (view.team) params.push(`team=${encodeURIComponent(view.team)}`);
  if (params.length === 0) return url;
  return url + (url.includes('?') ? '&' : '?') + params.join('&');
};

const logoutToLogin = () => {
  ['token', 'number', 'loginTime'].forEach((k) => localStorage.removeItem(k));
  if (window.location.pathname !== '/') window.location.assign('/');
};

// installCrmAuth patches window.fetch once so that every CRM call in the app (including
// screens written before roles existed) is authenticated and view-scoped the same way.
let installed = false;
export const installCrmAuth = () => {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (!url.startsWith(CRM_API_BASE_URL)) return nativeFetch(input, init);

    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined) || {});
    if (!headers.has('Authorization')) headers.set('Authorization', CRM_AUTH_TOKEN);
    const token = localStorage.getItem('token');
    if (token) headers.set('X-User-Token', token);

    const res = await nativeFetch(withView(url), { ...init, headers });
    // 401 = the login token is missing or expired (403 = logged in, not allowed).
    if (res.status === 401 && !url.includes('/users/numbers')) logoutToLogin();
    return res;
  };
};

// crm(path, { method, body, query }) -> the response's `data`, or throws an Error with
// the server's message. Use this for new code.
export const crm = async (path, { method = 'GET', body, query } = {}) => {
  let url = buildCrmApiUrl(path);
  if (query) {
    const qs = Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let json = null;
  try {
    json = await res.json();
  } catch (e) {
    json = null;
  }
  if (!res.ok || !json || json.success === false) {
    const err = new Error(json?.error || json?.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json.data;
};

// copyText works where navigator.clipboard is unavailable (older mobile browsers, in-app
// webviews, non-secure contexts).
export const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to the textarea method
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

export const inr = (v) => `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;

export const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

// "3h ago" / "in 2d"
export const fromNow = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const unit = abs < 3600e3 ? [60e3, 'm'] : abs < 86400e3 ? [3600e3, 'h'] : [86400e3, 'd'];
  const n = Math.max(1, Math.round(abs / unit[0]));
  return diff < 0 ? `${n}${unit[1]} ago` : `in ${n}${unit[1]}`;
};
